import { createContext, useContext, useSyncExternalStore } from "react";

/** Mobilnézet nagy képernyőn (kivetítéshez): bekapcsolva az alkalmazás asztalon is a
 *  telefonos elrendezést mutatja, középre igazított keretben (`core/ViewFrame.tsx`).
 *  Böngészőnként él (localStorage), így egy bemutató előtt beállítható, és újratöltés,
 *  újraindítás után is megmarad. */
export type ViewMode = "auto" | "mobile";

const KEY = "view-mode";
/** A keret ettől a szélességtől jelenik meg (a Tailwind `md` töréspontja); alatta az oldal
 *  amúgy is telefonos — telefonon soha nincs keret. */
const WIDE_QUERY = "(min-width: 48rem)";

function load(): ViewMode {
  try {
    return localStorage.getItem(KEY) === "mobile" ? "mobile" : "auto";
  } catch {
    return "auto"; // tiltott tároló (pl. szigorú privát mód)
  }
}

let current: ViewMode = load();
const listeners = new Set<() => void>();

export function setViewMode(mode: ViewMode): void {
  current = mode;
  try {
    if (mode === "mobile") localStorage.setItem(KEY, mode);
    else localStorage.removeItem(KEY);
  } catch {
    // tiltott tároló: ebben a fülben így is vált, csak nem jegyezzük meg
  }
  listeners.forEach((listener) => listener());
}

function subscribeMode(listener: () => void) {
  listeners.add(listener);
  // másik fülben átkapcsolva ez is kövesse
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY || e.key === null) {
      current = load();
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function useViewMode(): ViewMode {
  return useSyncExternalStore(subscribeMode, () => current);
}

// jsdom-ban nincs `matchMedia`: ott keskenynek számít az ablak.
function subscribeWide(listener: () => void) {
  if (typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia(WIDE_QUERY);
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
}

function isWide(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(WIDE_QUERY).matches;
}

/** Most éppen telefonkeretben fut-e az alkalmazás (a használati statisztika `frame` jelzése). */
export function isFramed(): boolean {
  return current === "mobile" && isWide();
}

/** Elég széles-e az ablak a kerethez (a mód bekapcsolásától független). */
export function useIsWide(): boolean {
  return useSyncExternalStore(subscribeWide, isWide);
}

/** A felcsúszó panelek (`Sheet`) portálja: a mobilnézet keretében a keret, különben a `body`. */
export const PortalContainer = createContext<HTMLElement | null>(null);

export function usePortalContainer(): HTMLElement | null {
  return useContext(PortalContainer);
}
